import { Button, Flex, Paper, Stack, Text, Title } from "@mantine/core";
import { Dispatch, SetStateAction, useEffect, useRef, useState } from "react";
import { useUpdateCustomerData } from "../../../hooks/ui/customerDataStore";
import {
  useStartSignUp,
  useStartSignIn,
  useStartLogin,
  useGetLoginStatus,
  useCancelTransaction,
} from "../../../hooks/api/";
import { v4 as uuidV4 } from "uuid";
import { useQueryClient } from "@tanstack/react-query";
import {
  usePOAStepsStore,
  ENUMPOASteps,
} from "../../../hooks/ui/poaStepsDataStore";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { FooterButtons } from "../../footerButtons";
import { BankIdQrCode } from "../../bankID/qrCode";
import { BankIdPendingMessage } from "../../login/pendingMessage";
import { LoaderComponent } from "../../loaderComponent";
import { isMobileDevice } from "../../../utils/useDeviceDetection";
import { navigateByCustomerStatus } from "../../../utils/navigateByCustomerStatus";
import { getBankIdFailureMessage } from "../../../utils/bankIdFailureMessages";
import { RetryErrorCard } from "../../bankID/retryErrorCard";

export const Identify = ({
  setUserCancelled,
}: {
  setUserCancelled: Dispatch<SetStateAction<boolean>>;
}) => {
  const { currentStep, setStep } = usePOAStepsStore();
  const [complete, setComplete] = useState(false);
  const [loginError, setLoginError] = useState(false);
  const [bankIdFailed, setBankIdFailed] = useState(false);
  const [failedHintCode, setFailedHintCode] = useState<string | null>(null);
  const hasInitializedLogin = useRef(false);
  const hasHandledComplete = useRef(false);
  const nonceRef = useRef<string | null>(null);
  const navigate = useNavigate();
  const router = useRouter();
  const queryClient = useQueryClient();
  const isMobile = isMobileDevice();

  const {
    customerData: { personalNumber, transactionId },
    updatePersonalNumber,
    updateTransactionId,
    updateName,
    updateCredentials,
    updateIdentityVerificationId,
  } = useUpdateCustomerData();
  const startLogin = useStartLogin();
  const cancelLogin = useCancelTransaction();
  const signIn = useStartSignIn();
  const signUp = useStartSignUp();

  const getLoginStatus = useGetLoginStatus({
    id: startLogin.data?.body?.id,
    enabled: startLogin.isSuccess && !complete,
  });

  const beginLogin = () => {
    const nonce = uuidV4();
    nonceRef.current = nonce;
    localStorage.setItem("bankid_login_nonce", nonce);
    sessionStorage.setItem("bankid_login_tab", "1");
    startLogin.mutate({
      nonce,
      returnPath: window.location.pathname + window.location.search,
    });
  };

  const processStatusInfo =
    getLoginStatus.data?.body?.providerInfo?.seBankID?.processStatusInfo;

  useEffect(() => {
    if (hasInitializedLogin.current) return;
    hasInitializedLogin.current = true;

    if (!startLogin.data) {
      beginLogin();
    }

    return () => {
      hasInitializedLogin.current = false;
    };
  }, []);

  // On iOS, the tab is fully suspended when BankID app opens.
  // Use refs to ensure the visibilitychange handler is stable (registered once)
  const completeRef = useRef(complete);
  completeRef.current = complete;
  const getLoginStatusRef = useRef(getLoginStatus);
  getLoginStatusRef.current = getLoginStatus;

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible" && !completeRef.current) {
        getLoginStatusRef.current.refetch();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  useEffect(() => {
    if (getLoginStatus.data?.body?.status === "failed") {
      setComplete(true);
      setBankIdFailed(true);
      setFailedHintCode(processStatusInfo ?? null);
    }
    if (getLoginStatus.isError) {
      setComplete(true);
      setLoginError(true);
    }
    if (
      getLoginStatus.data?.body?.status === "complete" &&
      !hasHandledComplete.current
    ) {
      hasHandledComplete.current = true;
      setComplete(true);
      const pn =
        getLoginStatus.data?.body?.providerInfo?.seBankID?.completionData?.user
          ?.personalNumber;
      const tid = getLoginStatus.data?.body?.id;
      const firstName =
        getLoginStatus.data?.body?.providerInfo?.seBankID?.completionData?.user
          ?.givenName;

      const lastName =
        getLoginStatus.data?.body?.providerInfo?.seBankID?.completionData?.user
          ?.surname;
      updateName(firstName, lastName);
      updatePersonalNumber(pn);
      updateTransactionId(tid);
      signIn.mutate({ personalNumber: pn, transactionId: tid });
    }
  }, [getLoginStatus.data?.body?.status, getLoginStatus.isError]);

  // Auto-retry signIn when it fails after a successful BankID auth.
  // iOS kills TCP connections during tab suspension, causing first request to fail.
  const signInRetryCount = useRef(0);
  useEffect(() => {
    if (
      signIn.isError &&
      getLoginStatus.data?.body?.status === "complete" &&
      signInRetryCount.current < 3
    ) {
      const personalNumber =
        getLoginStatus.data?.body?.providerInfo?.seBankID?.completionData?.user
          ?.personalNumber;
      const transactionId = getLoginStatus.data?.body?.id;
      if (personalNumber && transactionId) {
        const delay = Math.min(1000 * 2 ** signInRetryCount.current, 4000);
        const timer = setTimeout(() => {
          signInRetryCount.current += 1;
          signIn.mutate({ personalNumber, transactionId });
        }, delay);
        return () => clearTimeout(timer);
      }
    }
    if (signIn.isSuccess) {
      signInRetryCount.current = 0;
    }
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signIn.isError, signIn.isSuccess]);

  useEffect(() => {
    if (signIn.data?.isCustomer === false && !signIn.data?.credentials) {
      signUp.mutate({ personalNumber, transactionId });
    }

    if (signIn.isSuccess && signIn.data?.credentials) {
      updateCredentials(signIn.data.credentials);
      updateIdentityVerificationId(signIn.data?.identityVerificationId ?? null);

      if (signIn.data?.isCustomer) {
        navigateByCustomerStatus({
          credentials: signIn.data.credentials,
          queryClient,
          navigate,
          invalidate: () => router.invalidate(),
        });
      } else {
        // Not a customer - go directly to EMAILANDPHONE step (skip IDENTIFY)
        setStep(ENUMPOASteps.EMAILANDPHONE);
      }
    }
  }, [signIn.isSuccess, signIn.data]);

  useEffect(() => {
    if (signUp.isSuccess && signUp.data?.credentials) {
      updateCredentials(signUp.data.credentials);
      updateIdentityVerificationId(signUp.data?.identityVerificationId ?? null);
      // After signup, go directly to EMAILANDPHONE step
      setStep(ENUMPOASteps.EMAILANDPHONE);
    }
  }, [signUp.isSuccess, signUp.data]);

  if (
    signIn.isPending ||
    signUp.isPending ||
    (signIn.isSuccess && signIn.data?.isCustomer)
  ) {
    return <LoaderComponent />;
  }

  if (currentStep !== ENUMPOASteps.IDENTIFY) {
    return <LoaderComponent />;
  }

  if (loginError) {
    return (
      <Flex justify={"center"} px={"lg"} data-testid="poa-identity-error">
        <Stack>
          <RetryErrorCard
            title="Hoppsan! Något gick fel"
            description="Det gick inte att verifiera din identitet. Det kan bero på ett tillfälligt fel. Vänligen försök igen."
            onRetry={async () => {
              const oldTransactionId = startLogin.data?.body?.id;
              if (oldTransactionId) {
                await cancelLogin
                  .mutateAsync(oldTransactionId)
                  // Errors already reported to Sentry via useCancelTransaction's onError
                  .catch(() => {});
              }
              setComplete(false);
              setLoginError(false);
              hasInitializedLogin.current = false;
              hasHandledComplete.current = false;
              beginLogin();
            }}
          >
            <FooterButtons>
              <Button
                variant="default"
                onClick={() => setUserCancelled(true)}
                data-testid="cancel-button"
              >
                Avbryt
              </Button>
            </FooterButtons>
          </RetryErrorCard>
        </Stack>
      </Flex>
    );
  }

  if (bankIdFailed) {
    const failureMessage = getBankIdFailureMessage(failedHintCode);
    return (
      <Flex
        justify={"center"}
        px={"lg"}
        data-testid="poa-identity-bankid-failed"
      >
        <Stack>
          <RetryErrorCard
            title={failureMessage.title}
            description={failureMessage.text}
            onRetry={async () => {
              const oldTransactionId = startLogin.data?.body?.id;
              if (oldTransactionId) {
                await cancelLogin.mutateAsync(oldTransactionId).catch(() => {});
              }
              setComplete(false);
              setBankIdFailed(false);
              setFailedHintCode(null);
              hasInitializedLogin.current = false;
              hasHandledComplete.current = false;
              beginLogin();
            }}
          >
            <FooterButtons>
              <Button
                variant="default"
                onClick={() => setUserCancelled(true)}
                data-testid="cancel-button"
              >
                Avbryt
              </Button>
            </FooterButtons>
          </RetryErrorCard>
        </Stack>
      </Flex>
    );
  }

  if (
    !complete &&
    getLoginStatus.data?.body?.status === "started" &&
    (processStatusInfo === "userSign" || processStatusInfo === "started")
  ) {
    return (
      <BankIdPendingMessage
        onCancel={() => {
          setComplete(true);
        }}
        processStatusInfo={processStatusInfo}
      />
    );
  }

  return (
    <Flex justify={"center"} px={"lg"} data-testid="poa-identity">
      <Stack>
        <Title
          order={2}
          ff={"var(--wallet-font-family-sambla-sans)"}
          mb={"md"}
          mx={"sm"}
        >
          Var vänlig identifiera dig med BankID
        </Title>
        <Text mx={"sm"}>
          Genom att verifiera din identitet kan vi se till att din information
          är korrekt och att den bara delas med rätt person. På så sätt skyddar
          vi både dig och din personliga data.
        </Text>
        <Paper p={30} radius={"24px"} mb={"2rem"}>
          <BankIdQrCode
            isLoading={
              signUp.isPending ||
              signIn.isPending ||
              getLoginStatus.isLoading ||
              startLogin.isPending
            }
            qrData={getLoginStatus.data?.body?.providerInfo?.seBankID?.qrData}
            autoStartToken={
              getLoginStatus.data?.body?.providerInfo?.seBankID?.autoStartToken
            }
            instructionText={
              isMobile
                ? "Starta BankID-appen på din mobil"
                : "Starta BankID-appen på din mobila enhet och tryck på 'Skanna QR-kod'. Rikta kameran mot QR-koden och identifiera."
            }
            processStatusInfo={processStatusInfo}
            nonce={nonceRef.current}
          />

          <FooterButtons>
            <Button
              variant="default"
              onClick={() => setUserCancelled(true)}
              data-testid="cancel-button"
            >
              Avbryt
            </Button>
          </FooterButtons>
        </Paper>
      </Stack>
    </Flex>
  );
};
